// Components
import { Paper, Text, Group, Stack, Anchor } from "@mantine/core";
import { useMemo } from "react";

// Hooks
import { useCommunicationSenderName } from "@/hooks/domain/useCommunicationSenderName";

// Utils
import { formatFHIRDateTime } from "@benchmark/ui/utils";
import { getSafeHref } from "@/utils/ui/safe-url";

// Types
import type { SMSMessageProps } from "@/types/domain/messaging";

// Styles
import classes from "./SMSMessage.module.css";

/** Regex matching http://, https://, and www. URLs for linkification. */
const URL_REGEX = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/g;

/**
 * Converts text with URLs to React elements with clickable links.
 * @agentic connections
 */
function linkifyText(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  // Reset regex state
  URL_REGEX.lastIndex = 0;

  while ((match = URL_REGEX.exec(text)) !== null) {
    // Add text before the URL
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    // Add the URL as a link. Route through the shared scheme allowlist —
    // message bodies are untrusted input, so a value that fails the check
    // renders as plain text instead of a clickable link.
    const url = match[0];
    const href = getSafeHref(url.startsWith("www.") ? `https://${url}` : url);
    if (href) {
      parts.push(
        <Anchor
          key={match.index}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
        >
          {url}
        </Anchor>,
      );
    } else {
      parts.push(url);
    }

    lastIndex = match.index + url.length;
  }

  // Add remaining text after last URL
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
}

/**
 * Displays an SMS message in a chat bubble with linkified URLs.
 * @agentic connections
 */
export const SMSMessage = ({ communication, isOutbound }: SMSMessageProps) => {
  // Extract message content
  const messageText = communication.payload?.[0]?.contentString || "";

  // Resolve sender name (handles PractitionerRole → Practitioner resolution)
  const { senderName } = useCommunicationSenderName(communication);

  // Defensive: optimistic sends now start as `completed` (Dialpad accepted),
  // so this "Sending…" branch is only reached by any Communication that arrives
  // with an `in-progress` status from another source.
  const isSending = communication.status === "in-progress";

  // Extract timestamp
  const timestamp = communication.sent || communication.meta?.lastUpdated;

  // Format timestamp
  const formattedTime = useMemo(() => {
    if (!timestamp) return "";
    try {
      return formatFHIRDateTime(timestamp);
    } catch {
      return "";
    }
  }, [timestamp]);

  // Linkify message text
  const messageContent = useMemo(() => linkifyText(messageText), [messageText]);

  return (
    <Group
      justify={isOutbound ? "flex-end" : "flex-start"}
      align="flex-start"
      wrap="nowrap"
      mb="sm"
      className={classes.messageGroup}
    >
      <Paper
        p="sm"
        radius="md"
        className={
          isOutbound ? classes.outboundMessage : classes.inboundMessage
        }
        style={{
          maxWidth: "70%",
          opacity: isSending ? 0.7 : 1,
        }}
      >
        <Stack gap="xs">
          {/* Message content with linkified URLs */}
          <Text
            size="sm"
            style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
          >
            {messageContent}
          </Text>

          {/* Metadata: sender and timestamp */}
          <Group justify="space-between" gap="xs">
            <Text size="xs" className={classes.metaText}>
              {isSending ? "Sending..." : senderName}
            </Text>
            {formattedTime && !isSending && (
              <Text size="xs" className={classes.metaText}>
                {formattedTime}
              </Text>
            )}
          </Group>
        </Stack>
      </Paper>
    </Group>
  );
};
